import { Field, Form, Formik } from 'formik'
import Header from 'components/headers/addLoan'
import AddNewIcon from 'assets/images/loans/managementAddNew.svg'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Num2persian from 'utilities/helper/num2Persian'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import useGetCards from 'pages/loans/withDraw/hooks/useGetCards'
import SelectModal from 'components/modals/selectModal'
import WithDrawWalletModal from 'components/modals/withDrawWallets'
import FeeModal from 'components/modals/feeModal'
import CustomDatePicker from 'components/datePicker'
import { Schema } from './utilities'
import posthog from 'posthog-js'
import moment from 'jalali-moment'
import { getOneMonthLater } from 'utilities/helper/getOneMonthLater'
import { toJalaliInputDate } from 'utilities/helper/jalaliInputDate'
import { loanInterestFields } from 'utilities/helper/loanInterest'
import useSetNewLoans from './hooks/useSetNewLoans'
import useGuarantorSuggestion from './hooks/useGuarantorSuggestion'
import MemberProfilePanel from './MemberProfilePanel'
import { NumericFormat } from 'react-number-format'
import GuarantorPickerModal, {
    IGuarantorOption,
} from 'components/modals/guarantorPicker'
import {
    accountNumberOf,
    formatAccountNumber,
} from 'components/bankAccountCard'

interface ISelectUser {
    name: string
    id: undefined | number
    memberId: undefined | number
}

interface IGrantFormValues {
    amount: string
    tag: string
    number: string
    fee_type: string
    date: string
    bankAccount: string
}

/**
 * What the screen had collected when it last sent the manager on. Typed rather
 * than read off `location.state` as `any`, so Formik keeps inferring a
 * concrete shape for `values` and `errors`.
 */
interface IGrantFormDraft {
    values?: Partial<IGrantFormValues>
    selectedUser?: ISelectUser
    selectedFee?: number
    selectedFeeType?: string
    guarantors?: Array<IGuarantorOption>
    bankAccountId?: number
}
export default function AddNewManagementLoan({}) {
    const { state, pathname } = useLocation()
    /**
     * What this screen had collected when it last sent the manager on to the
     * instalments step. It is written into THIS history entry (see the
     * `navigate(..., { replace: true })` in `onSubmit`), so every way back —
     * the «بازگشت» button, the header arrow, the phone's own back gesture —
     * lands on a form that still has the answers in it. Reported from real
     * use: «وقتی … برای اصلاح برگشت رو می‌زنیم، کل فرم پاک می‌شه».
     */
    const draft: IGrantFormDraft | undefined = state?.draft
    const [isOpenReceiver, setIsOpenReceiver] = useState<boolean>(false)
    const [selectedUser, setSelectedUser] = useState<ISelectUser | undefined>(
        draft?.selectedUser
    )
    const [isOpenWalletModal, setIsOpenWalletModal] = useState<boolean>(false)
    const [selectedBankNumber, setSelectedBankNumber] = useState<number>(0)
    const [memberId, setMemberId] = useState<undefined | number>()
    const [isOpenFeeModal, setIsOpenFeeModal] = useState<boolean>(false)
    const [guarantors, setGuarantors] = useState<Array<IGuarantorOption>>(
        draft?.guarantors ?? []
    )
    const [isOpenGuarantors, setIsOpenGuarantors] = useState<boolean>(false)
    const [selectedFee, setSelectedFee] = useState<number | undefined>(
        draft?.selectedFee ?? 0
    )
    const [selectedFeeType, setSelectedFeeType] = useState<string>(
        draft?.selectedFeeType ?? 'percentage'
    )
    const navigate = useNavigate()
    const params = useParams()

    const {
        data,
        isLoading,
        activeAccount,
        activeAccounts,
        otherAccounts,
        handleSelectCard,
        reallySelectedCard,
    } = useGetCards(
        selectedUser?.id,
        selectedUser?.memberId,
        setSelectedBankNumber,
        draft?.bankAccountId
    )
    const { mutate } = useSetNewLoans()
    const guarantorSuggestion = useGuarantorSuggestion(
        params?.id,
        selectedUser?.id
    )
    return (
        <>
            <Formik
                initialValues={{
                    amount: draft?.values?.amount ?? '',
                    tag: draft?.values?.tag ?? '',
                    number: draft?.values?.number ?? '',
                    fee_type: draft?.values?.fee_type ?? '',
                    // Prefilled with a month from today rather than left
                    // empty: an empty picker rendered TODAY, so the screen
                    // showed one first-instalment date and the API was sent
                    // another (this field falls back to `getOneMonthLater()`
                    // when blank). Android has always opened on next month.
                    date: draft?.values?.date ?? getOneMonthLater(),
                    bankAccount: '',
                }}
                validationSchema={Schema}
                onSubmit={(values) => {
                    if (Number(values.amount) >= 5000) {
                        if (selectedUser) {
                            if (
                                selectedFee !== undefined &&
                                selectedFee !== null
                            ) {
                                if (selectedFee === 0) {
                                    values.fee_type = 'lip_ma'
                                }
                                if (values.fee_type.length > 0) {
                                    const data: IManagementBodyFirst = {
                                        moneyPool: params?.id,
                                        poolShip: selectedUser.id,
                                        amount: values.amount,
                                        tag: values.tag,
                                        number_of_repayments: values.number,
                                        bank_account:
                                            reallySelectedCard &&
                                            reallySelectedCard.id,
                                        bankName:
                                            reallySelectedCard &&
                                            reallySelectedCard.full_name +
                                                '-' +
                                                reallySelectedCard.bank_name,
                                        // carried for the confirmation screen
                                        // only — the API call picks its
                                        // fields by name
                                        bankAccount: reallySelectedCard,
                                        ctx_id: params && Number(params.id),
                                        receiver: selectedUser?.memberId,
                                        name:
                                            selectedUser && selectedUser?.name,
                                        interest_pay_mode: values.fee_type,
                                        ...loanInterestFields(
                                            selectedFeeType,
                                            selectedFee
                                        ),
                                        pay_date: moment().format('YYYY-MM-DD'),
                                        fundName: state?.name,
                                        due_date:
                                            values.date.length > 0
                                                ? values.date
                                                : getOneMonthLater(),
                                        guarantor_ids: guarantors.map(
                                            (item) => item.poolship_id
                                        ),
                                        guarantor_names: guarantors.map(
                                            (item) => item.name
                                        ),
                                    }
                                    posthog.capture('loan_grant_started', {
                                        flow: 'legacy_form',
                                        fund_id: params?.id,
                                        poolship_id: params?.poolShip,
                                        // the COUNT only — `guarantor_names`
                                        // is right there in the payload above
                                        // and must never follow it into an
                                        // event property
                                        guarantor_count: guarantors.length,
                                    })
                                    // park the answers on this history
                                    // entry before leaving, so coming back
                                    // finds them
                                    navigate(pathname, {
                                        replace: true,
                                        state: {
                                            ...(state ?? {}),
                                            draft: {
                                                values,
                                                selectedUser,
                                                selectedFee,
                                                selectedFeeType,
                                                guarantors,
                                                bankAccountId:
                                                    reallySelectedCard?.id,
                                            },
                                        },
                                    })
                                    mutate(data)
                                } else {
                                    toast.error(
                                        'نحوه دریافت کارمزد را انتخاب کنید'
                                    )
                                }
                            } else {
                                toast.error('میزان کارمزد را وارد کنید')
                            }
                        } else {
                            toast.error('دریافت کننده وام را انتخاب کنید')
                        }
                    } else {
                        toast.error('حداقل مبلغ وام پنج هزار تومان می باشد')
                    }
                }}
            >
                {({ values, touched, errors, setFieldValue }) => (
                    <Form>
                        <div className=" mx-3 flex flex-col h-screen text-sm ">
                            <div className=" grow-0">
                                <Header title="اعطای وام" />
                            </div>
                            <div className=" rounded-lg grow">
                                <div className="  bg-white  mt-14 rounded-lg relative py-4 shadow-[0px_2px_6px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)]">
                                    <div className=" text-center mt-4">
                                        <img
                                            src={AddNewIcon}
                                            alt="add-loan"
                                            className=" absolute right-[35%] md:right-[40%] lg:right-[40%] -top-14"
                                        />
                                        <p className=" mb-2">موجودی صندوق:</p>
                                        <p className=" text-black">
                                            {state &&
                                                Number(
                                                    state.exist
                                                ).toLocaleString()}{' '}
                                            تومان
                                        </p>
                                    </div>
                                    <div className="  w-full text-center">
                                        <NumericFormat
                                            customInput={Field}
                                            thousandSeparator
                                            allowNegative={false}
                                            name="amount"
                                            placeholder="مبلغ وام (تومان)"
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3  "
                                            value={values.amount}
                                            isGray
                                            secondaryTitle="| تومان "
                                            onValueChange={(values) => {
                                                const { value } = values
                                                setFieldValue('amount', value)
                                            }}
                                        />

                                        {values.amount !== '' && (
                                            <span className=" text-xs text-blue-1 text-center w-full">
                                                {Num2persian(
                                                    values.amount.toString()
                                                )}{' '}
                                                تومان
                                            </span>
                                        )}
                                        {errors.amount && touched.amount && (
                                            <p className=" text-sm text-error-3 text-center">
                                                {errors.amount}
                                            </p>
                                        )}

                                        <div
                                            onClick={() =>
                                                setIsOpenReceiver(true)
                                            }
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {selectedUser?.name
                                                ? selectedUser?.name
                                                : 'دریافت کننده وام'}
                                        </div>
                                        {/* the borrower's record with the
                                            box, right where the decision is
                                            made. Facts only — it never
                                            blocks the grant. */}
                                        <MemberProfilePanel
                                            moneypoolId={params?.id}
                                            poolshipId={selectedUser?.id}
                                        />
                                        <div
                                            onClick={() => {
                                                if (!selectedUser) {
                                                    toast.error(
                                                        'ابتدا دریافت کننده وام را انتخاب کنید'
                                                    )
                                                    return
                                                }
                                                posthog.capture(
                                                    'loan_guarantor_picker_opened',
                                                    {
                                                        fund_id: params?.id,
                                                        source: 'grant_legacy',
                                                        has_suggested:
                                                            !!guarantorSuggestion,
                                                        current_count:
                                                            guarantors.length,
                                                    }
                                                )
                                                setIsOpenGuarantors(true)
                                            }}
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {guarantors.length > 0 ? (
                                                <span className=" text-black">
                                                    ضامن:{' '}
                                                    {guarantors
                                                        .map(
                                                            (item) => item.name
                                                        )
                                                        .join('، ')}
                                                </span>
                                            ) : (
                                                'ضامن وام (اختیاری)'
                                            )}
                                        </div>
                                        <div className=" w-full text-center">
                                            <Field
                                                type="text"
                                                inputMode="numeric"
                                                className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3  "
                                                name="number"
                                                placeholder="تعداد اقساط"
                                                min={0}
                                            />
                                            {errors.number &&
                                                touched.number && (
                                                    <p className=" text-sm text-error-3 text-center">
                                                        {errors.number}
                                                    </p>
                                                )}
                                        </div>
                                        <div
                                            onClick={() =>
                                                setIsOpenFeeModal(true)
                                            }
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {selectedFee !== undefined &&
                                            selectedFee !== null &&
                                            selectedFee !== 0
                                                ? `${selectedFee} ${
                                                      selectedFeeType ===
                                                      'percentage'
                                                          ? 'درصد'
                                                          : 'تومان'
                                                  }`
                                                : 'میزان کارمزد'}
                                        </div>
                                        {selectedFee !== 0 && (
                                            <Field
                                                name="fee_type"
                                                as="select"
                                                className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3"
                                            >
                                                <option className="text-gray-1">
                                                    نحوه دریافت کارمزد
                                                </option>
                                                <option value="lip_ma">
                                                    افزودن به هر قسط
                                                </option>
                                                <option value="lip_pr">
                                                    کسر از مبلغ وام
                                                </option>
                                            </Field>
                                        )}
                                        <div
                                            dir="rtl"
                                            className="border-b border-dashed border-gray-1"
                                        >
                                            <>
                                                <CustomDatePicker
                                                    value={toJalaliInputDate(
                                                        values.date
                                                    )}
                                                    setValue={(e: string) => {
                                                        setFieldValue('date', e)
                                                    }}
                                                    name={'date'}
                                                    title="تاریخ شروع اقساط"
                                                    placeHolder="تاریخ شروع اقساط"
                                                />
                                            </>
                                        </div>
                                        {/* The number, not just the name of
                                            whose account it is — «وام تنها
                                            ثبت شود» on the next screen means
                                            the manager transfers the money
                                            themselves. */}
                                        <div
                                            onClick={() => {
                                                selectedUser
                                                    ? setIsOpenWalletModal(true)
                                                    : toast.error(
                                                          'دریافت کننده را انتخاب کنید'
                                                      )
                                            }}
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {reallySelectedCard ? (
                                                <>
                                                    <span>
                                                        {reallySelectedCard.full_name +
                                                            '-' +
                                                            reallySelectedCard.bank_name}
                                                    </span>
                                                    {accountNumberOf(
                                                        reallySelectedCard
                                                    ) && (
                                                        <span
                                                            dir="ltr"
                                                            className=" mt-1 block text-black tracking-wide"
                                                        >
                                                            {formatAccountNumber(
                                                                accountNumberOf(
                                                                    reallySelectedCard
                                                                )!.value
                                                            )}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                'شماره حساب'
                                            )}
                                        </div>
                                        <Field
                                            as="textarea"
                                            name="tag"
                                            className="w-full outline-0 text-center py-4 px-4 text-sm"
                                            placeholder="توضیحات(اختیاری)"
                                        />
                                        {errors.tag && touched.tag && (
                                            <p className=" text-sm text-error-3 text-center">
                                                {errors.tag}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                className=" grow-0 bg-green-1 text-white py-3 rounded-lg"
                                type="submit"
                            >
                                تایید
                            </button>
                            <button
                                type="button"
                                className=" text-center my-3 text-gray-4"
                                onClick={() => navigate(-1)}
                            >
                                بعدا
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            <SelectModal
                show={isOpenReceiver}
                onHide={() => setIsOpenReceiver(false)}
                setSelectedUser={setSelectedUser}
                setMemberId={setMemberId}
            />
            <GuarantorPickerModal
                show={isOpenGuarantors}
                onHide={() => setIsOpenGuarantors(false)}
                moneypoolId={params?.id}
                value={guarantors}
                onChange={setGuarantors}
                excludePoolshipId={selectedUser?.id}
                suggested={guarantorSuggestion}
            />
            <WithDrawWalletModal
                show={isOpenWalletModal}
                onHide={() => setIsOpenWalletModal(false)}
                data={!isLoading && data && data.receivers[0].bankaccounts}
                activeAccounts={activeAccounts}
                otherAccounts={otherAccounts}
                selectedUser={selectedUser?.name}
                setSelectedBankNumber={setSelectedBankNumber}
                handleSelectCard={handleSelectCard}
            />
            <FeeModal
                show={isOpenFeeModal}
                onHide={() => setIsOpenFeeModal(false)}
                setSelectedFee={setSelectedFee}
                setSelectedFeeType={setSelectedFeeType}
            />
        </>
    )
}
